import { useCallback, useRef, memo, FC, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { emptyText } from '@utils/constants';
import { useScroll } from '@hooks/useScroll';
import { useElementSize } from '@hooks/useElementSize';
import {
  useLazyGetChatMessagesByShowIdQuery,
  useLazyGetPinnedChatMessagesByShowIdQuery,
} from '@redux/queries/chat';
import {
  useShowChatMessageIds,
  useShowChatMessagesMayLoadMore,
  usePinnedChatMessageByShowId,
} from '@redux/selectors/chat';
import { DefaultText } from '@components/text';
import { useShowDataById } from '@redux/selectors/shows';
import { ShowStatus } from '@typings/show';

import ChatForm from './ChatForm';
import ChatMessage from './ChatMessage';

const Container = styled.div<{ $isEmpty: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px;
  background-color: ${({ theme }) =>
    `${theme.colors.black}${theme.opacity[40]}`};
  overflow: hidden;
  gap: 8px;
`;

const PinnedMessageContainer = styled.div`
  margin-bottom: 0;
`;

const MessagesList = styled.div<{ $inverted?: boolean }>`
  display: flex;
  overflow-y: scroll;
  flex-direction: column;
  height: 100%;
  transform: scaleY(-1);
  ${({ theme }) => theme.scrollbar.hidden}

  & > * {
    transform: scaleY(-1);
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scaleY(-1) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scaleY(-1) translateY(0);
    }
  }
`;

const EmptyMessage = styled(DefaultText)`
  align-self: center;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.grey[400]};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
`;

type Props = {
  showId: string;
  isShownChatForm?: boolean;
};

const DynamicChat: FC<Props> = ({ showId, isShownChatForm }) => {
  const ids = useShowChatMessageIds(showId);
  const mayLoadMore = useShowChatMessagesMayLoadMore(showId);
  const pinnedMessageId = usePinnedChatMessageByShowId(showId);
  const showData = useShowDataById(showId);
  const isLiveShow = useMemo(
    () => showData?.status === ShowStatus.Live,
    [showData?.status],
  );
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementSize(containerRef, {
    widthOnly: true,
  });

  const [getChatMessagesByShowId] = useLazyGetChatMessagesByShowIdQuery();
  const [getPinnedChatMessagesByShowId] =
    useLazyGetPinnedChatMessagesByShowIdQuery();

  const renderItem = useCallback(
    (messageId: string) => (
      <ChatMessage
        key={messageId}
        messageId={messageId}
        width={containerWidth}
      />
    ),
    [containerWidth],
  );

  const onLoadMore = useCallback(() => {
    if (mayLoadMore) {
      getChatMessagesByShowId({
        showId,
        lastId: ids[ids.length - 1],
        isRefresh: false,
      });
    }
  }, [mayLoadMore, getChatMessagesByShowId, showId, ids]);

  useEffect(() => {
    if (!showId) {
      return;
    }

    getChatMessagesByShowId({
      showId,
      isRefresh: true,
    });
    if (isLiveShow) {
      getPinnedChatMessagesByShowId({ showId });
    }
  }, [
    getChatMessagesByShowId,
    showId,
    getPinnedChatMessagesByShowId,
    isLiveShow,
  ]);

  useEffect(() => {
    if (listRef.current && ids.length > 0) {
      listRef.current.scrollTop = 0;
    }
  }, [ids.length]);

  useScroll({ onLoadMore, containerRef: listRef, isInverted: true });

  return (
    <Container $isEmpty={!ids.length} ref={containerRef}>
      {!!pinnedMessageId && (
        <PinnedMessageContainer>
          <ChatMessage messageId={pinnedMessageId} pinned />
        </PinnedMessageContainer>
      )}

      {!ids.length ? (
        <EmptyMessage>{emptyText.emptyChat}</EmptyMessage>
      ) : (
        <MessagesList ref={listRef} $inverted>
          {ids.map(renderItem)}
        </MessagesList>
      )}

      {isShownChatForm ? <ChatForm showId={showId} /> : null}
    </Container>
  );
};

export default memo(DynamicChat);
